import type { PopperProps } from "@mui/base";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { MaxKeywordLength } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import { pathname as searchPathname } from "scmp-app/pages/search";

import { useDebouncedAutoComplete } from "./hooks";
import {
  Container,
  FullWidthBackground,
  StyledInput as input,
  StyledAutocomplete,
  StyledBaseInput,
  StyledClose,
  StyledInputAdornment,
  StyledMagnifier,
} from "./styles";

export type Props = {
  className?: string;
  onChange?: (value: string) => void;
};

export const SearchInput: FunctionComponent<Props> = ({ className, onChange }) => {
  const { currentValue, searchRouteFunction } = useSearchContext();
  const router = useRouter();

  // Prevent it will refresh the search result when the user type in the input box
  const [committedSearchTerm, setCommittedSearchTerm] = useState<null | string>(currentValue);
  const [searchInput, setSearchInput] = useState("");
  const { fetchSuggestions, resetSuggestions, suggestionsState } = useDebouncedAutoComplete();
  const suggestions = suggestionsState.result;

  useEffect(() => {
    async function fetchData() {
      await fetchSuggestions(searchInput);
    }
    void fetchData();
  }, [fetchSuggestions, searchInput]);

  const handleSearch = useCallback(
    (value: null | string) => {
      if (!notEmpty(value?.trim())) return;
      const searchPageRoute = searchRouteFunction?.({ q: value.trim() });
      if (!searchPageRoute) return;

      const { asPath, ...restSearchPageRoute } = searchPageRoute;
      void router.push(restSearchPageRoute, asPath);
    },
    [router, searchRouteFunction],
  );

  const handleReset = useCallback(() => {
    setSearchInput("");
    setCommittedSearchTerm("");
    resetSuggestions();
    const searchPageRoute = searchRouteFunction?.({ value: "" });
    if (!searchPageRoute) return;

    void router.push(searchPageRoute, searchPathname);
  }, [router, searchRouteFunction, resetSuggestions]);

  const isShowCloseButton = useMemo(
    () => notEmpty(currentValue) && committedSearchTerm,
    [currentValue, committedSearchTerm],
  );

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  const popperModifiers = useMemo<PopperProps["modifiers"]>(
    () => [
      {
        name: "flip",
        options: {
          fallbackPlacements: [],
        },
      },
      {
        enabled: true,
        fn: ({ state }) => {
          state.elements.popper.style.inlineSize = "calc(100% + 20px)";
        },
        name: "customInlineSize",
        phase: "afterWrite",
      },
    ],
    [],
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
      option: unknown,
      { inputValue }: { inputValue: string },
    ) => {
      const { key, ...restProps } = props;
      if (typeof option !== "string") return null;
      const parts = option.split(new RegExp(`(${inputValue})`, "ig"));
      return (
        <li key={key} {...restProps}>
          <div>
            {parts.map((part, index) =>
              part.toLowerCase() === inputValue.toLowerCase() ? (
                <strong key={index}>{part}</strong>
              ) : (
                <span key={index}>{part}</span>
              ),
            )}
          </div>
        </li>
      );
    },
    [],
  );

  useEffect(() => {
    const queryParameter = sanitizeKeyword(router.query.q as string | undefined);
    if (queryParameter === undefined) {
      setSearchInput("");
      setCommittedSearchTerm("");
    } else {
      setSearchInput(queryParameter);
      setCommittedSearchTerm(queryParameter);
    }
  }, [router.query.q]);

  return (
    <Container $stickyStackedHeight={appBarStickyHeight} className={className}>
      <FullWidthBackground />
      <StyledAutocomplete
        disablePortal
        freeSolo
        onChange={(event, value) => {
          const newValue = value as null | string;
          setCommittedSearchTerm(newValue);
          handleSearch(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          onChange?.(newInputValue);
          setSearchInput(newInputValue);
        }}
        options={suggestions}
        renderInput={parameters => (
          <StyledBaseInput
            {...parameters.InputProps}
            endAdornment={
              <StyledInputAdornment>
                {isShowCloseButton ? (
                  <StyledClose
                    onClick={handleReset}
                    onMouseDown={event => event.stopPropagation()}
                  />
                ) : (
                  <StyledMagnifier
                    onClick={() => {
                      setCommittedSearchTerm(searchInput);
                      handleSearch(searchInput);
                    }}
                    onMouseDown={event => event.stopPropagation()}
                  />
                )}
              </StyledInputAdornment>
            }
            placeholder="Search SCMP"
            slotProps={{
              input: {
                ...parameters.inputProps,
                maxLength: MaxKeywordLength,
              },
            }}
            slots={{ input }}
          />
        )}
        renderOption={renderOption}
        slotProps={{
          paper: {
            style: {
              border: suggestions.length > 0 ? "1px solid #e5e5e5" : "none",
              borderRadius: 0,
              boxShadow: "none",
            },
          },
          popper: {
            popperOptions: {
              modifiers: popperModifiers,
              placement: "bottom",
            },
          },
        }}
        value={committedSearchTerm}
      />
    </Container>
  );
};
